import { callApi } from './api';
import BlogPost, { BlogAuthor, BlogFAQItem, BlogPostSeo } from '@/models/blog-post';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const blogApiUrl = import.meta.env.VITE_API_URL_V1 + '/blog';

// Admin reads go through `/blog/admin*` — gated by admin role (not subscription
// plan), drafts included. POST/PUT/DELETE stay on `/blog*` (already admin-gated).
const blogAdminApiUrl = `${blogApiUrl}/admin`;

export const getBlogPosts = async () => {
    return callApi<BlogPost[]>({ url: `${blogAdminApiUrl}`, method: 'GET' });
};

export const useBlogPosts = () => {
    return useQuery({ queryKey: ['blogPosts'], queryFn: getBlogPosts });
};

export const getBlogPost = async (postId: string) => {
    return callApi<BlogPost>({ url: `${blogAdminApiUrl}/${postId}`, method: 'GET' });
};

export const useBlogPost = (postId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['blogPost', postId],
        queryFn: () => getBlogPost(postId),
        enabled: !!postId && isOpen,
    });
};

/**
 * Only editable fields go over the wire. The API's validate() middleware
 * rejects unmatched body keys, so internal read-only fields (id, created_at,
 * updated_at, deleted) must NOT be sent. coverImage / seo.ogImage are PERMANENT
 * public URLs (from /files/permanent) stored verbatim and baked into the site
 * HTML — so they ARE sent (unlike the courses fileId-resolved pattern).
 */
export type BlogPostMutationPayload = {
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    categoryId: string;
    tags: string[];
    author: BlogAuthor;
    publishedAt?: string;
    readTime?: number;
    seo: Pick<BlogPostSeo, 'metaTitle' | 'metaDescription' | 'ogImage'>;
    faq?: BlogFAQItem[];
    coverImage?: string;
    coverImageAlt?: string;
};

export const createBlogPost = async (payload: BlogPostMutationPayload) => {
    return callApi<BlogPost>({ url: `${blogApiUrl}`, method: 'POST', data: payload });
};

export const useCreateBlogPost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createBlogPost,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
        },
    });
};

export const updateBlogPost = async ({ postId, ...payload }: BlogPostMutationPayload & { postId: string }) => {
    return callApi<BlogPost>({ url: `${blogApiUrl}/${postId}`, method: 'PUT', data: payload });
};

export const useUpdateBlogPost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateBlogPost,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
            queryClient.invalidateQueries({ queryKey: ['blogPost', variables.postId] });
        },
    });
};

export const deleteBlogPost = async ({ postId }: { postId: string }) => {
    return callApi({ url: `${blogApiUrl}/${postId}`, method: 'DELETE' });
};

export const useDeleteBlogPost = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteBlogPost,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
        },
    });
};
